import { Component, OnDestroy, OnInit } from '@angular/core';
import { User } from '@angular/fire/auth';
import { Firestore } from '@angular/fire/firestore';
import { ActivatedRoute, Router } from '@angular/router';

import { ChannelWithDocId } from '@yoimo/client-sdk/channels';
import { EventWithDocId } from '@yoimo/client-sdk/events';
import { VideoWithDocId, getVideo$ } from '@yoimo/client-sdk/videos';
import { VideoListModule } from '@yoimo/interfaces';
import { UIState, WindowService } from '@yoimo/joymo-ui';

import {
  BehaviorSubject,
  EMPTY,
  Observable,
  ReplaySubject,
  Subject,
  catchError,
  combineLatest,
  delay,
  filter,
  first,
  map,
  of,
  shareReplay,
  startWith,
  switchMap,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs';

import { VideoPageScope } from '@core/interfaces/types';
import {
  AuthService,
  PlatformService,
  logger,
  TicketOptionsService,
} from '@core/services';
import {
  AccessControlService,
  VideoAccessCheckFailureReasons as FailureReasons,
} from '@core/services/access-control.service';

import { isRecommendedBrowser, isMobile } from 'shared-lib/core';

import { VideoPlayerTrigger } from './video-feedback/video-feedback.component';
import {
  VideoAccessStatus,
  VideoErrorState,
  VIDEO_ERROR_STATES,
} from './video.utils';

@Component({
  selector: 'joymo-video',
  templateUrl: './video.component.html',
  styleUrls: ['./video.scss'],
})
export class VideoComponent implements OnInit, OnDestroy {
  /** In case the video image fails to load */
  invalidPosterImage = false;
  isMobile = false;
  showBrowserRecommendation = false;
  showChannelVideoList = true;
  showEventLiveUpcoming = true;
  /** Util flag to force re-render child components */
  canBeRendered?: boolean;
  accessStatus?: VideoAccessStatus;
  errorState?: VideoErrorState;
  state: UIState = 'idle';

  readonly pageScope$: Observable<VideoPageScope>;
  readonly video$ = new BehaviorSubject<VideoWithDocId | null>(null);
  readonly user$ = this.authService.user$;

  private readonly destroy$ = new Subject<void>();
  readonly isAboutToStart$ = new ReplaySubject<boolean>(1);
  readonly isPlayerInFullScreen$ = new ReplaySubject<boolean>(1);
  readonly isServer = this.platformService.isServer();
  readonly nextVideoLinkLabel = $localize`:@@VideoPageNextLinkLabel:Up next`;
  readonly previousVideoLinkLabel = $localize`:@@VideoPagePreviousLinkLabel:Previous`;
  readonly videoPlayerTrigger$ = new Subject<VideoPlayerTrigger>();
  readonly videoListForChannel: VideoListModule = {
    heading: $localize`:@@VideoPageExploreVideosListTitle:Explore`,
    moduleType: 'VIDEOS_LIST',
    listSource: {},
    layoutType: 'CAROUSEL',
    density: 'DENSE',
    size: 6,
    sort: 'DATE',
    order: 'DESC',
  };

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private fs: Firestore,
    private authService: AuthService,
    private windowService: WindowService,
    private platformService: PlatformService,
    private accessControlService: AccessControlService,
    private ticketOptionsService: TicketOptionsService
  ) {
    this.pageScope$ = this.getPageScope$().pipe(shareReplay(1));
  }

  ngOnInit() {
    if (this.isServer) return;

    this.isMobile = isMobile();
    this.showBrowserRecommendation = isRecommendedBrowser() === false;

    this.initializeForcedDisconnectHandler();

    const onPaymentSuccess$ = this.ticketOptionsService.onPaymentResult$.pipe(
      filter((res) => res === 'success')
    );
    /**
     * Manage most of the component state
     *
     * Check viewer access to the video - if has access and video is live or VOD, show the player.
     * If video is still scheduled and will start soon, wait for its state to be updated and then show the player.
     */
    combineLatest([this.pageScope$, onPaymentSuccess$.pipe(startWith(null))])
      .pipe(
        switchMap(([scope]) => {
          this.state = 'loading';
          return this.getAccessStatus$(scope.video);
        }),
        switchMap((res) => {
          this.accessStatus = res;

          if (!res.access) {
            this.state = 'error';
            this.errorState = this.getErrorDetails(res.reason);
            return EMPTY;
          }
          return this.isAboutToStart$.pipe(
            map((startsSoon) => [res, startsSoon] as const)
          );
        }),
        switchMap(([res, startsSoon]) => {
          this.video$.next(res.video);

          if (startsSoon) return this.waitForVideoUpdates$(res.video);
          return of(res.video);
        }),
        switchMap((video) => this.getAccessStatus$(video)),
        takeUntil(this.destroy$)
      )
      .subscribe((res) => {
        this.state = 'ready';
        this.video$.next(null);
        if (!res.access) return;
        this.video$.next(res.video);
      });
  }

  openTicketOptions(
    channel: ChannelWithDocId,
    video: VideoWithDocId,
    event: EventWithDocId | undefined,
    user: User | null,
    accessStatus: VideoAccessStatus | undefined
  ): string | void {
    if (!accessStatus || accessStatus.access) return;

    if (video.unlocked && !user) {
      const authUrl = this.authService.getAuthenticationUrl(
        channel.docId,
        channel.slug,
        this.windowService.document.location.href
      );

      this.windowService.document.location.assign(authUrl);
      return authUrl;
    }
    this.ticketOptionsService.openAllPurchaseOptions(
      event,
      video,
      accessStatus.isSubscriptionPaused
    );
  }

  handleErrorStateAction(
    scope: VideoPageScope,
    errorCode?: VideoErrorState['reason']
  ): void {
    if (errorCode === 'NEEDS_ACTION') {
      this.router.navigate([scope.homepageUrl, 'profile', 'subscriptions']);
      return;
    }
    throw new Error('NotImplemented:' + errorCode);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getErrorDetails(reason: FailureReasons): VideoErrorState | undefined {
    if (reason === 'NO_ACCESS' || reason === 'SUBSCRIPTION_PAUSED') {
      return undefined;
    }
    return { details: VIDEO_ERROR_STATES[reason], reason };
  }

  /** Makes sure the player is destroyed when the user gets disconnected */
  private initializeForcedDisconnectHandler(): void {
    this.authService.forcedDisconnect$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.accessStatus = undefined;
        this.video$.next(null);
      });
  }

  private waitForVideoUpdates$(
    video: VideoWithDocId
  ): Observable<VideoWithDocId> {
    return getVideo$(this.fs, video.docId, video.channelId, this.destroy$).pipe(
      first((update): update is VideoWithDocId => {
        return (
          update?.streamState !== 'SCHEDULED' && update?.videoLocation !== 'NA'
        );
      }),
      delay(30000) // wait for video playlist to be generated
    );
  }

  private getAccessStatus$(
    video: VideoWithDocId
  ): Observable<VideoAccessStatus> {
    if (video.archived) {
      return of({
        access: false,
        isPurchaseOptionsAvailable: false,
        isSubscriptionPaused: false,
        reason: 'CONTENT_GONE',
      });
    }

    return this.accessControlService.checkAccessToVideo(video).pipe(
      catchError((err) => {
        logger.logCriticalError(err, 'CONTENT-ACCESS');

        return of({
          access: false,
          reason: 'ACCESS_FAILED',
          isOptionsAvailable: false,
        } as const);
      }),
      map((res) => {
        if (res.access) {
          return {
            access: true,
            playerContext: res.playerContext,
            accessType: res.accessType,
            video: { ...video, videoLocation: res.videoUrl! },
          };
        }

        return {
          access: false,
          isPurchaseOptionsAvailable: res.isOptionsAvailable,
          isSubscriptionPaused: res.reason === 'SUBSCRIPTION_PAUSED',
          reason: res.reason,
        };
      })
    );
  }

  private getPageScope$(): Observable<VideoPageScope> {
    return this.route.data.pipe(
      tap(() => (this.canBeRendered = false)),
      delay(1),
      map((data) => {
        // Reset properties
        this.state = 'idle';
        this.accessStatus = undefined;
        this.isAboutToStart$.next(false);
        this.isPlayerInFullScreen$.next(false);
        this.canBeRendered = true;
        this.errorState = undefined;
        this.invalidPosterImage = false;
        this.showChannelVideoList = true;
        this.showEventLiveUpcoming = true;
        this.video$.next(null);
        return data['videoPageScope'] as VideoPageScope;
      })
    );
  }
}
